import React from 'react';

/**
 * Meter component for displaying scalar measurements within a known range.
 * All native <meter> attributes are supported via props.
 *
 * Additional possible attributes:
 * - form: string (form owner)
 * - name: string (form field name)
 * - labels: ReactNode (for accessibility, not a native attribute)
 * - style/className: for custom styling
 *
 * See: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/meter
 */
export interface MeterProps {
  value: number;
  min?: number;
  max?: number;
  low?: number;
  high?: number;
  optimum?: number;
  id?: string;
  className?: string;
  form?: string;
  name?: string;
  style?: React.CSSProperties;
  labels?: React.ReactNode;
  /**
   * Any other props for extensibility
   */
  [key: string]: any;
}

const Meter: React.FC<MeterProps> = ({
  value,
  min = 0,
  max = 100,
  low,
  high,
  optimum,
  id,
  className,
  form,
  name,
  style,
  labels,
  ...rest
}) => {
  const meterClass = className ? `meter ${className}` : 'meter';
  return (
    <>
      {labels}
      <meter
        value={value}
        min={min}
        max={max}
        low={low}
        high={high}
        optimum={optimum}
        id={id}
        className={meterClass}
        form={form}
        {...(name ? { name } : {})}
        style={style}
        {...rest}
      />
    </>
  );
};

/**
 * Example usage:
 * <Meter value={75} min={0} max={100} low={50} high={80} optimum={100} />
 */

export default Meter;
